import React, { FormEvent, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Stack,
  Paper,
  CircularProgress,
  Checkbox,
  FormGroup,
  TextField,
  Alert,
} from '@mui/material';
import {
  useSuitabilityQuestions,
  useSubmitSuitabilityTest,
  useLastSuitabilitySubmission,
} from '@/lib/api/suitability.ts';
import { SuitabilityAnswerDTO } from '@inversor-hub/types';

export default function SuitabilityTestPage() {
  const { data: questions, isLoading: loadingQuestions } = useSuitabilityQuestions();
  const { data: lastSubmission, isLoading: loadingLastSubmission } = useLastSuitabilitySubmission();
  const submitMutation = useSubmitSuitabilityTest();

  const [answers, setAnswers] = useState<Record<number, number | number[] | null>>({});
  const [otherTexts, setOtherTexts] = useState<Record<number, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [showingPrevious, setShowingPrevious] = useState(false);
  const [timeLeft, setTimeLeft] = useState<string>('');
  const [canRetake, setCanRetake] = useState(true);

  // Countdown timer
  useEffect(() => {
    if (lastSubmission?.canRetryAt && !lastSubmission.passed) {
      const retryDate = new Date(lastSubmission.canRetryAt);
      const now = new Date();

      if (now < retryDate) {
        setCanRetake(false);

        const timer = setInterval(() => {
          const now = new Date();
          const diff = retryDate.getTime() - now.getTime();

          if (diff <= 0) {
            setCanRetake(true);
            setTimeLeft('');
            clearInterval(timer);
          } else {
            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diff % (1000 * 60)) / 1000);

            setTimeLeft(`${days}d ${hours}h ${minutes}m ${seconds}s`);
          }
        }, 1000);

        return () => clearInterval(timer);
      } else {
        setCanRetake(true);
      }
    }
  }, [lastSubmission]);

  // Load previous answers if lastSubmission is present
  React.useEffect(() => {
    if (questions && lastSubmission) {
      setShowingPrevious(true);
      const prevAnswers: Record<number, number | number[]> = {};
      const prevTexts: Record<number, string> = {};
      lastSubmission.answers.forEach((answer) => {
        const qId = answer.questionId;
        const question = questions.find((q) => q.id === qId);
        if (answer.otherText) {
          prevTexts[qId] = answer.otherText;
          if (!answer.optionId && question) {
            const otherOption = question.options.find((opt) => opt.text.includes('Otro:'));
            if (otherOption) {
              if (question.type === 'multi') {
                prevAnswers[qId] = prevAnswers[qId]
                  ? [...(prevAnswers[qId] as number[]), otherOption.id]
                  : [otherOption.id];
              } else {
                prevAnswers[qId] = otherOption.id;
              }
            }
          }
        }
        if (answer.optionId) {
          if (question?.type === 'multi') {
            prevAnswers[qId] = prevAnswers[qId]
              ? [...(prevAnswers[qId] as number[]), answer.optionId]
              : [answer.optionId];
          } else {
            prevAnswers[qId] = answer.optionId;
          }
        }
      });
      setAnswers(prevAnswers);
      setOtherTexts(prevTexts);
    }
  }, [questions, lastSubmission]);

  const handleSingleChange = (questionId: number, optionId: number) => {
    setAnswers((prev) => ({ ...prev, [questionId]: optionId }));
  };

  const handleMultiChange = (questionId: number, optionId: number, checked: boolean) => {
    setAnswers((prev) => {
      const current = (prev[questionId] as number[]) || [];
      if (checked) {
        return { ...prev, [questionId]: [...current, optionId] };
      } else {
        return { ...prev, [questionId]: current.filter((id) => id !== optionId) };
      }
    });
  };

  const handleOtherTextChange = (questionId: number, text: string) => {
    setOtherTexts((prev) => ({ ...prev, [questionId]: text }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!questions) return;
    const answersArray: SuitabilityAnswerDTO[] = Object.entries(answers).flatMap(
      ([questionId, answer]) => {
        const qId = Number(questionId);
        const question = questions.find((q) => q.id === qId);
        const otherText = otherTexts[qId];
        if (!question) return [];
        if (Array.isArray(answer)) {
          // Multi-select question
          return answer
            .map((optionId) => {
              const option = question.options.find((opt) => opt.id === optionId);
              const isOtherOption = option?.text.includes('Otro:');
              return {
                questionId: qId,
                optionId,
                score: option?.score || 0,
                otherText: isOtherOption && otherText ? otherText : undefined,
              };
            })
            .filter((ans) => ans.optionId || ans.otherText);
        } else {
          // Single-select question
          const option = question.options.find((opt) => opt.id === answer);
          const isOtherOption = option?.text.includes('Otro:');
          return [
            {
              questionId: qId,
              score: option?.score || 0,
              optionId: answer as number,
              otherText: isOtherOption && otherText ? otherText : undefined,
            },
          ];
        }
      }
    );
    try {
      await submitMutation.mutateAsync(answersArray);
      setSubmitted(true);
      // Scroll to top to show result messages
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      // Optionally show error feedback
      // eslint-disable-next-line no-console
      console.error('Error submitting test:', error);
    }
  };

  if (loadingQuestions || loadingLastSubmission) return <CircularProgress />;
  if (!questions) return <Typography>Error cargando preguntas.</Typography>;

  return (
    <Box maxWidth={600} mx="auto" mt={4}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Test de Idoneidad
        </Typography>

        {lastSubmission && !canRetake && (
          <Box sx={{ mb: 3 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Necesitas esperar {timeLeft} para poder hacer de nuevo el test
              </Typography>
              <Typography variant="body2">
                Te aconsejamos realices un curso de formación para inversores
              </Typography>
            </Alert>
          </Box>
        )}

        {lastSubmission && showingPrevious && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1, mb: 2 }}>
              <Typography variant="body2" color="info.dark">
                Mostrando tus respuestas del{' '}
                {new Date(lastSubmission.createdAt).toLocaleDateString()}
              </Typography>
            </Box>
            <Box
              sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: lastSubmission.passed ? 'success.light' : 'error.light',
                border: 2,
                borderColor: lastSubmission.passed ? 'success.main' : 'error.main',
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: lastSubmission.passed ? 'common.black' : 'common.white',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  mb: 1,
                }}
              >
                {lastSubmission.passed ? '✅ TEST APROBADO' : '❌ TEST NO APROBADO'}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: lastSubmission.passed ? 'common.black' : 'common.white',
                  textAlign: 'center',
                }}
              >
                Puntuación: {lastSubmission.score || 0} puntos
                {lastSubmission.passed
                  ? ' - Cumples con los requisitos de idoneidad'
                  : ' - No cumples con los requisitos mínimos (15 puntos)'}
              </Typography>
            </Box>
          </Box>
        )}
        {(!submitted && canRetake) || (lastSubmission && !canRetake) ? (
          <form onSubmit={handleSubmit}>
            <Stack spacing={4}>
              {questions.map((q) => (
                <Box key={q.id}>
                  <Typography variant="h6" gutterBottom>
                    {q.text}
                  </Typography>

                  {q.type === 'single' ? (
                    <RadioGroup
                      value={answers[q.id] ?? ''}
                      onChange={(e) => handleSingleChange(q.id, Number(e.target.value))}
                    >
                      {q.options?.map((opt) => (
                        <FormControlLabel
                          key={opt.id}
                          value={opt.id}
                          control={<Radio disabled={showingPrevious || !canRetake} />}
                          label={opt.text}
                        />
                      ))}
                    </RadioGroup>
                  ) : (
                    <FormGroup>
                      {q.options?.map((opt) => (
                        <FormControlLabel
                          key={opt.id}
                          control={
                            <Checkbox
                              disabled={showingPrevious || !canRetake}
                              checked={((answers[q.id] as number[]) || []).includes(opt.id)}
                              onChange={(e) => handleMultiChange(q.id, opt.id, e.target.checked)}
                            />
                          }
                          label={opt.text}
                        />
                      ))}
                      {q.options?.some((opt) => opt.text.includes('Otro:')) &&
                        ((answers[q.id] as number[]) || []).some((optId) =>
                          q.options?.find((opt) => opt.id === optId)?.text.includes('Otro:')
                        ) && (
                          <TextField
                            size="small"
                            placeholder="Especificar..."
                            disabled={showingPrevious || !canRetake}
                            value={otherTexts[q.id] || ''}
                            onChange={(e) => handleOtherTextChange(q.id, e.target.value)}
                            sx={{ mt: 1, maxWidth: 300 }}
                          />
                        )}
                    </FormGroup>
                  )}
                </Box>
              ))}
              {!showingPrevious && canRetake ? (
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={
                    Object.keys(answers).length < questions.length - 1 ||
                    submitMutation.status === 'pending'
                  }
                >
                  {submitMutation.status === 'pending' ? (
                    <CircularProgress size={24} />
                  ) : (
                    'Enviar test'
                  )}
                </Button>
              ) : canRetake ? (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    setShowingPrevious(false);
                    setAnswers({});
                    setOtherTexts({});
                  }}
                >
                  Repetir test
                </Button>
              ) : null}
            </Stack>
          </form>
        ) : canRetake ? (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="success.main" gutterBottom>
              ¡Gracias! Tu test ha sido enviado y evaluado.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Recarga la página para ver tu resultado.
            </Typography>
          </Box>
        ) : null}
      </Paper>
    </Box>
  );
}
